import {
  isEmpty,
  pipe,
  mergeRight,
  find,
  propEq,
  curry,
  lensProp,
  set,
  view,
  not,
} from "ramda";
import {
  completeStep,
  getErrors,
  getField,
  initialStepState,
  setCompleted,
  setErrors,
  setField,
  setFields,
  setLoadingLocalStorage,
} from "common/stepReducerCommon";
import * as introActions from "intro/introActions";
import * as basicDetailsActions from "basic-details/basicDetailsActions";
import * as submissionActions from "submission/submissionActions";
import { validateIntro, validateIntroField } from "intro/introValidation";
import { getDefaultLibraryId } from "application/applicationReducer";
import * as steps from "../common/steps";
import { isBlank } from "common/general";
import * as applicationReducer from "application/applicationReducer";

const initialIntroStepState = {
  showLocalLibraryOther: false,
  fields: {
    isSubmitterCreator: true,
    country: {},
    region: {},
    localLibrary: {},
  },
};

const initialState = mergeRight(initialStepState, initialIntroStepState);

const submitterIsCreatorChanged = (value, applicationState, state) => {
  return pipe(
    setFields(initialState.fields),
    setField(["isSubmitterCreator"], value),
    setCompleted(false),
    updateDefaultLocalLibrary(getDefaultLibraryId(applicationState))
  )(state);
};

const subdivisionsLens = lensProp("subdivisions");
const setSubdivisions = set(subdivisionsLens);
export const getSubdivisions = view(subdivisionsLens);

const countryChanged = (country, state) => {
  return pipe(
    setField(["country"], country),
    setField(["region"], {}),
    setSubdivisions(initialState.subdivisions),
    setCompleted(false)
  )(state);
};

const regionChanged = (region, state) => {
  return pipe(setField(["region"], region), setCompleted(false))(state);
};

const roleChanged = (role, state) => {
  return pipe(setField(["role"], role), setCompleted(false))(state);
};

const localLibraryChanged = curry((library, state) => {
  return setField(["localLibrary"], library || {}, state);
});

const librariesLens = lensProp("libraries");
const setLibraries = set(librariesLens);
export const getLibraries = view(librariesLens);

const showLocalLibraryOtherLens = lensProp("showLocalLibraryOther");
export const getShowLocalLibraryOther = view(showLocalLibraryOtherLens);

export const isSubmitterCreator = getField(["isSubmitterCreator"]);

const fieldOptions = (appState) => applicationReducer.getFieldOptions(appState);

const fieldBlurred = (field, value, fieldOptions, state) =>
  setErrors(validateIntroField(field, value, getErrors(state)), state);

const fieldChanged = (path, value, state) =>
  pipe(setField(path, value), setCompleted(false))(state);

const localStorageLoaded = (data, state) =>
  pipe(
    setLoadingLocalStorage(false),
    setFields(isEmpty(data) ? initialState.fields : data.fields),
    setCompleted(isEmpty(data) ? initialState.completed : data.completed)
  )(state);

const updateDefaultLocalLibrary = curry((defaultLocalLibraryId, state) => {
  const localLibrary = getField(["localLibrary"], state);
  const libraries = getLibraries(state);
  const defaultLibrary = find(
    propEq("organizationId", defaultLocalLibraryId),
    libraries
  );

  return isEmpty(localLibrary) && not(isBlank(defaultLibrary))
    ? localLibraryChanged(defaultLibrary, state)
    : state;
});

const cancelSubmission = pipe(
  setCompleted(false),
  setFields(initialState.fields),
  setErrors(initialState.errors)
);

const introReducer = (state = initialState, action, applicationState) => {
  switch (action.type) {
    case introActions.COMPLETE_STEP:
    case submissionActions.SUBMIT:
      return completeStep(validateIntro(fieldOptions(applicationState)), state);

    case introActions.SUBMITTER_IS_CONTRIBUTOR_CHANGED:
      return submitterIsCreatorChanged(action.value, applicationState, state);

    case introActions.COUNTRY_CHANGED:
      return countryChanged(action.country, state);

    case introActions.FIELD_BLURRED:
      return fieldBlurred(
        action.field,
        action.value,
        fieldOptions(applicationState),
        state
      );

    case introActions.FIELD_CHANGED:
      return fieldChanged(action.path, action.value, state);

    case introActions.LOCAL_LIBRARY_CHANGED:
      return pipe(
        localLibraryChanged(action.library),
        setCompleted(false)
      )(state);

    case introActions.REGION_CHANGED:
      return regionChanged(action.region, state);

    case introActions.ROLE_CHANGED:
      return roleChanged(action.role, state);

    case introActions.LOCAL_STORAGE_LOADED:
      return localStorageLoaded(action.data, state);

    case submissionActions.CANCEL_SUBMISSION:
      return cancelSubmission(state);

    case basicDetailsActions.LIBRARIES_LOADED: {
      return pipe(
        setLibraries(action.libraries),
        updateDefaultLocalLibrary(getDefaultLibraryId(applicationState))
      )(state);
    }

    case submissionActions.SET_ACTIVE_STEP:
      return action.step === steps.INTRO ? setCompleted(false)(state) : state;

    default:
      return state;
  }
};

export default introReducer;
